import clsx from "clsx";

import { DateTimeTz, Icon } from "@web-app/components";
import { RouteTimeline } from "@web-app/types";
import { timeZoneMap } from "@web-app/utils/helpers";

type OrdersOrderItem = {
  displayName?: string;
  id: string;
  index: number;
  type: string;
};

type RouteOrdersActivityPropsType = {
  item: RouteTimeline;
  showTimelineBar: boolean;
  timezone: keyof typeof timeZoneMap | undefined;
};

export default function RouteOrdersActivity({
  item,
  showTimelineBar,
  timezone,
}: RouteOrdersActivityPropsType) {
  // Parse ordersOrder from previousValue and updatedValue JSON strings
  let previousOrdersOrder: OrdersOrderItem[] = [];
  let updatedOrdersOrder: OrdersOrderItem[] = [];

  if (item.previousValue) {
    try {
      previousOrdersOrder = JSON.parse(item.previousValue);
    } catch (error) {
      console.error("Failed to parse ordersOrder from previousValue:", error);
    }
  }

  if (item.updatedValue) {
    try {
      updatedOrdersOrder = JSON.parse(item.updatedValue);
    } catch (error) {
      console.error("Failed to parse ordersOrder from updatedValue:", error);
    }
  }

  // Create a map of order ID to previous index
  const previousIndexMap = new Map<string, number>();
  previousOrdersOrder.forEach((orderItem, index) => {
    previousIndexMap.set(orderItem.id, index);
  });

  // Create a set of updated order IDs for tracking removed orders
  const updatedOrderIds = new Set(updatedOrdersOrder.map((item) => item.id));

  // Generate position change messages for updated/added orders
  const positionChanges = updatedOrdersOrder.map((orderItem, newIndex) => {
    const orderName = orderItem.displayName || orderItem.id;
    const previousIndex = previousIndexMap.get(orderItem.id);

    if (previousIndex !== undefined && previousIndex !== newIndex) {
      return `${orderName} was moved from position ${previousIndex + 1} to ${newIndex + 1}`;
    } else if (previousIndex === undefined) {
      return `${orderName} was added at position ${newIndex + 1}`;
    } else {
      return null;
    }
  });

  // Find removed orders (in previous but not in updated)
  const removedOrders = previousOrdersOrder
    .filter((orderItem) => !updatedOrderIds.has(orderItem.id))
    .map((orderItem) => {
      const orderName = orderItem.displayName || orderItem.id;
      const previousIndex = previousIndexMap.get(orderItem.id);
      return `${orderName} was removed from position ${(previousIndex ?? 0) + 1}`;
    });

  // Combine all changes
  const allChanges = [...positionChanges, ...removedOrders].filter(Boolean);

  return (
    <li key={item.id} className="relative flex gap-x-4">
      <div
        className={clsx(
          showTimelineBar ? "-bottom-6" : "h-6",
          "absolute left-0 top-0 flex w-6 justify-center",
        )}
      >
        <div className="w-px bg-gray-200" />
      </div>
      <div className="relative flex h-7 w-6 flex-none items-center justify-center bg-white">
        <Icon name="fasRoute" className="h-4 text-status-green" />
      </div>
      <div className="flex-auto flex flex-col items-start py-0.5 text-xs leading-5 text-gray-500">
        <span className="inline-block font-medium text-status-green">
          Order positions updated:
        </span>
        <ul className="list-none ml-2 mt-1 text-gray-700">
          {allChanges.map((message, index) =>
            message ? <li key={index}>{message}</li> : null,
          )}
        </ul>
        <span className="relative mt-1">
          <DateTimeTz
            start={item?.createdAt}
            timezone={timezone}
            createdBy={item?.createdBy?.name}
            presentation="timeline"
          />
        </span>
      </div>
    </li>
  );
}
